import { env, json, yaml, type GeneratedFile } from './files';

export type AgentPlatform = 'windows' | 'linux' | 'container';

export interface AgentInput {
  /** `wss://…/tunnels/ws`. */
  gatewayUrl: string;
  clientId: string;
  /** The gateway's shared secret (legacy). */
  token?: string;
  /** AUTH client (`automation-gw:tunnel`), containers only — bare-metal agents enroll over their socket. */
  auth?: { clientId: string; clientSecret: string };
}

/** The agent's `appsettings.json` (next to Agent.exe / in the install dir). */
export function agentSettings(a: AgentInput): Record<string, unknown> {
  return {
    GatewayUrl: a.gatewayUrl,
    ClientId: a.clientId,
    ...(a.token ? { Token: a.token } : {}),
    ...(a.auth ? { Auth: { ClientId: a.auth.clientId, ClientSecret: a.auth.clientSecret } } : {}),
  };
}

export function agentEnv(a: AgentInput): Record<string, string> {
  return {
    AGENT__GATEWAYURL: a.gatewayUrl,
    AGENT__CLIENTID: a.clientId,
    ...(a.token ? { AGENT__TOKEN: a.token } : {}),
    ...(a.auth
      ? { AGENT__AUTH__CLIENTID: a.auth.clientId, AGENT__AUTH__CLIENTSECRET: a.auth.clientSecret }
      : {}),
  };
}

export function agentCompose(image: string, a: AgentInput): string {
  const vars = Object.entries(agentEnv(a))
    .map(([k, v]) => `      ${k}: ${yaml(v)}`)
    .join('\n');
  return `# NYCE.LOGIC automation agent in a container:  docker compose up -d
services:
  agent:
    image: ${image}
    container_name: nycelogic-agent-${a.clientId.toLowerCase().replace(/[^a-z0-9-]/g, '-')}
    restart: unless-stopped
    environment:
${vars}
      AGENT_RUNTIME_PATH: /data
    volumes:
      - agent-data:/data
volumes:
  agent-data:
`;
}

export const agentInstallWindows = `# In an elevated PowerShell, in the folder of the unpacked agent-bundle-…-win-x64.zip
# with this appsettings.json next to install.ps1:
powershell -ExecutionPolicy Bypass -File .\\install.ps1 -ConfigFile .\\appsettings.json
# Service "NyceLogicAgent". Upgrade later: .\\install.ps1   Remove: .\\install.ps1 -Uninstall
`;

export const agentInstallLinux = `# As root, in the folder of the unpacked agent-bundle-…-linux-x64.tar.gz
# with this appsettings.json next to install.sh:
sudo ./install.sh --config ./appsettings.json
# systemd unit nyce-logic-agent. Upgrade later: sudo ./install.sh   Remove: sudo ./install.sh --uninstall
`;

export function agentFiles(a: AgentInput, platform: AgentPlatform, image: string): GeneratedFile[] {
  if (platform === 'container')
    return [
      { name: 'docker-compose.yml', content: agentCompose(image, a), note: 'docker compose up -d' },
      {
        name: 'agent.env',
        content: `${Object.entries(agentEnv(a))
          .map(([k, v]) => `${k}=${env(v)}`)
          .join('\n')}\n`,
        note: 'The same settings as an env file (Kubernetes secret, docker run --env-file).',
      },
    ];
  return [
    {
      name: 'appsettings.json',
      content: json(agentSettings(a)),
      note: `Put it next to ${platform === 'windows' ? 'install.ps1' : 'install.sh'} in the unpacked bundle.`,
    },
    {
      name: 'install.txt',
      content: platform === 'windows' ? agentInstallWindows : agentInstallLinux,
      note: 'Run on the machine that hosts the agent.',
    },
  ];
}
